import React, { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Search as SearchIcon, X } from "lucide-react";
import PropertyCard from "@/components/PropertyCard";
import BrandHeading from "@/components/brand/BrandHeading";
import BrandLabel from "@/components/brand/BrandLabel";
import ResultsViewToggle from "@/components/search/ResultsViewToggle";
import LazyMapView from "@/components/search/LazyMapView";
import { unifiedSearch } from "@/lib/api";
import { cn } from "@/lib/utils";

const BRAND_PILLS = [
  { value: "", label: "All brands" },
  { value: "castlewood", label: "Castlewood" },
  { value: "vela", label: "Vela" },
  { value: "brionj", label: "Brionj" },
];

// Accent colour per brand — used for the chip on each result row's brand label.
const BRAND_ACCENT = {
  castlewood: "#7A2828",
  vela: "#1F4E5F",
  brionj: "#C5B188",
};

const BRAND_LABEL = {
  castlewood: "Castlewood",
  vela: "Vela",
  brionj: "Brionj",
};

// Normalise for case-insensitive, diacritic-tolerant partial matching.
// "rügen" / "rugen" / "RÜGEN" all collapse to "rugen".
const normalise = (s) =>
  (s || "")
    .toString()
    .normalize("NFKD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase();

const UnifiedSearch = () => {
  const [params, setParams] = useSearchParams();
  const [all, setAll] = useState([]);
  const [loading, setLoading] = useState(true);

  const brand = params.get("brand") || "";
  const initialQ = params.get("q") || "";

  // Two layers of query state:
  //  - `input` is what the user is typing (updated on every keystroke)
  //  - `debouncedInput` is what we actually filter against (175ms debounce)
  // This gives instant typing feedback while avoiding flicker on every key.
  const [input, setInput] = useState(initialQ);
  const [debouncedInput, setDebouncedInput] = useState(initialQ);

  // Fetch the full portfolio ONCE on mount, then filter client-side.
  // The catalog is small (17 items) — instant filtering with no network round-trip per keystroke.
  useEffect(() => {
    setLoading(true);
    unifiedSearch({})
      .then((d) => setAll(d.results || []))
      .finally(() => setLoading(false));
  }, []);

  // Debounce the input → debouncedInput by ~175ms.
  useEffect(() => {
    const t = setTimeout(() => setDebouncedInput(input), 175);
    return () => clearTimeout(t);
  }, [input]);

  // Keep the URL's ?q= in sync with the debounced input so the search is shareable
  // and survives a refresh, without thrashing history on every keystroke.
  useEffect(() => {
    const next = new URLSearchParams(params);
    if (debouncedInput) next.set("q", debouncedInput);
    else next.delete("q");
    setParams(next, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedInput]);

  // Brand-pill filter (clientside, alongside the text filter).
  const setBrand = (b) => {
    const next = new URLSearchParams(params);
    if (b) next.set("brand", b);
    else next.delete("brand");
    setParams(next, { replace: true });
  };

  // The actual filter: case-insensitive, diacritic-tolerant partial match across
  // name, location, country, brand slug, brand display name, and tagline.
  const filtered = useMemo(() => {
    const q = normalise(debouncedInput.trim());
    let list = all;
    if (brand) list = list.filter((r) => r.brand === brand);
    if (!q) return list;
    return list.filter((r) => {
      const haystack = [
        r.name,
        r.location,
        r.country,
        r.brand,
        BRAND_LABEL[r.brand],
        r.tagline,
      ]
        .map(normalise)
        .join(" \u0001 ");
      return haystack.includes(q);
    });
  }, [all, brand, debouncedInput]);

  const grouped = {
    castlewood: filtered.filter((r) => r.brand === "castlewood"),
    vela: filtered.filter((r) => r.brand === "vela"),
    brionj: filtered.filter((r) => r.brand === "brionj"),
  };

  const totalCount = filtered.length;
  const hasQuery = debouncedInput.trim().length > 0;

  // View state — defaults to List on first load per spec. The map is
  // lazy-loaded only when the user toggles to it (see LazyMapView).
  const [view, setView] = useState("list");

  // Map dataset — Castlewood + Vela hotels only. Brionj private residences
  // are intentionally excluded from the map view (they're enquiry-led, not
  // pin-on-a-map browse). The list view still shows them; the Map toggle
  // simply hides itself when the active brand filter is Brionj since the
  // map would be empty in that context.
  const mapItems = useMemo(
    () => filtered.filter((it) => it.brand !== "brionj"),
    [filtered]
  );
  const mapToggleAvailable = brand !== "brionj";

  // If the user switches to Brionj while in Map view, snap back to List
  // automatically — otherwise the map toggle would vanish underneath them
  // and the page would render no results.
  useEffect(() => {
    if (!mapToggleAvailable && view === "map") setView("list");
  }, [mapToggleAvailable, view]);

  return (
    <div className="container-rail py-10 md:py-16" data-testid="unified-search-page">
      <BrandLabel withRule>Across the portfolio</BrandLabel>
      <BrandHeading level={1} className="mt-4">Find a stay</BrandHeading>
      <p className="mt-3 max-w-[58ch] text-muted-foreground">
        Search across Castlewood country hotels, Vela coastal retreats, and Brionj private residences.
      </p>

      {/* Live search input — dynamic as the user types. */}
      <div className="mt-8 max-w-[680px]">
        <label htmlFor="unified-q" className="sr-only">Search the portfolio</label>
        <div className="relative">
          <SearchIcon
            size={16}
            className="absolute left-4 top-1/2 -translate-y-1/2 text-muted-foreground pointer-events-none"
            aria-hidden="true"
          />
          <input
            id="unified-q"
            type="text"
            value={input}
            onChange={(e) => setInput(e.target.value)}
            placeholder="Search by name, city, region, country, or brand — try “schloss”, “sylt”, “villa”…"
            className="w-full h-14 pl-11 pr-11 bg-card border border-border text-[15px] font-body focus:outline-none focus:border-foreground transition-colors rounded-[6px]"
            data-testid="unified-search-input"
            autoComplete="off"
            spellCheck="false"
          />
          {input && (
            <button
              type="button"
              onClick={() => setInput("")}
              className="absolute right-3 top-1/2 -translate-y-1/2 w-8 h-8 inline-flex items-center justify-center text-muted-foreground hover:text-foreground transition-colors"
              aria-label="Clear search"
              data-testid="unified-search-clear"
            >
              <X size={14} />
            </button>
          )}
        </div>
        <div className="mt-2 text-[12px] text-muted-foreground" aria-live="polite" data-testid="unified-search-summary">
          {loading
            ? "Loading the portfolio…"
            : hasQuery
              ? `${totalCount} ${totalCount === 1 ? "match" : "matches"} for “${debouncedInput.trim()}”`
              : `Browsing all ${all.length} stays across the portfolio.`}
        </div>
      </div>

      {/* Brand pills filter row */}
      <div className="mt-6 flex flex-wrap items-center justify-between gap-3" data-testid="brand-pills-row">
        <div className="flex flex-wrap gap-2" data-testid="brand-pills">
          {BRAND_PILLS.map((p) => (
            <button
              key={p.value || "all"}
              onClick={() => setBrand(p.value)}
              className={cn(
                "px-4 py-2 text-[12px] uppercase tracking-[0.16em] border transition-colors",
                brand === p.value
                  ? "bg-foreground text-background border-foreground"
                  : "border-border text-muted-foreground hover:text-foreground hover:border-foreground/40"
              )}
              data-testid={`brand-pill-${p.value || "all"}`}
            >
              {p.label}
            </button>
          ))}
        </div>
        {mapToggleAvailable && (
          <ResultsViewToggle view={view} onChange={setView} />
        )}
      </div>

      {/* ---- MAP VIEW ---- */}
      {view === "map" && mapToggleAvailable && (
        <div className="mt-8" data-testid="map-results">
          <LazyMapView items={mapItems} initialBounds="world" height={640} />
          <div className="mt-3 text-[12px] text-muted-foreground">
            Showing {mapItems.length} {mapItems.length === 1 ? "hotel" : "hotels"} on the map.
            Brionj private residences are available by enquiry —{" "}
            <button
              type="button"
              onClick={() => setView("list")}
              className="underline underline-offset-[3px] hover:text-foreground"
              data-testid="map-view-brionj-hint"
            >
              switch to list view
            </button>{" "}
            to browse them.
          </div>
        </div>
      )}

      {/* ---- LIST VIEW (default) ---- */}
      {view === "list" && (
        <div className="mt-12 space-y-16">
        {(brand === "" || brand === "castlewood") && grouped.castlewood.length > 0 && (
          <BrandGroup
            slug="castlewood"
            label="Castlewood"
            unit="hotels"
            count={grouped.castlewood.length}
            items={grouped.castlewood}
          />
        )}
        {(brand === "" || brand === "vela") && grouped.vela.length > 0 && (
          <BrandGroup
            slug="vela"
            label="Vela"
            unit="hotels"
            count={grouped.vela.length}
            items={grouped.vela}
          />
        )}
        {(brand === "" || brand === "brionj") && grouped.brionj.length > 0 && (
          <BrandGroup
            slug="brionj"
            label="Brionj"
            unit="residences"
            count={grouped.brionj.length}
            items={grouped.brionj}
          />
        )}

        {!loading && filtered.length === 0 && (
          <div
            className="py-16 text-center"
            data-testid="empty-state"
          >
            <div className="font-display text-[24px] md:text-[28px] text-foreground">
              No properties match that search.
            </div>
            <p className="mt-3 text-[14px] text-muted-foreground max-w-[40ch] mx-auto">
              Try a city, a region, a brand name, or part of a property name.
            </p>
            {hasQuery && (
              <button
                type="button"
                onClick={() => setInput("")}
                className="mt-6 inline-flex items-center gap-2 text-[12px] uppercase tracking-[0.16em] underline underline-offset-[6px] hover:text-foreground"
                data-testid="empty-clear"
              >
                Clear search
              </button>
            )}
          </div>
        )}
        </div>
      )}
    </div>
  );
};

// ---- Brand-grouped result section ----------------------------------------
const BrandGroup = ({ slug, label, unit, count, items }) => {
  const accent = BRAND_ACCENT[slug] || "#999";
  return (
    <section data-testid={`results-${slug}`}>
      <div className="flex items-end justify-between border-b border-border pb-3">
        <div className="inline-flex items-center gap-3">
          <span
            className="inline-block w-2.5 h-2.5 rounded-full"
            style={{ backgroundColor: accent }}
            aria-hidden="true"
          />
          <BrandLabel>{label}</BrandLabel>
        </div>
        <span className="text-[12px] text-muted-foreground">
          {count} {unit}
        </span>
      </div>
      <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
        {items.map((r) => (
          <PropertyCard key={r.id} item={r} showBrandBadge />
        ))}
      </div>
    </section>
  );
};

export default UnifiedSearch;
